import { ref, readonly } from "vue";

export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastItem {
  id: number;
  text: string;
  type: ToastType;
  duration: number;
}

const toasts = ref<ToastItem[]>([]);
const timers = new Map<number, ReturnType<typeof setTimeout>>();
let nextId = 0;

function dismiss(id: number): void {
  const idx = toasts.value.findIndex((t) => t.id === id);
  if (idx >= 0) toasts.value.splice(idx, 1);
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
}

function push(text: string, type: ToastType, duration = 3000): number {
  const id = nextId++;
  toasts.value.push({ id, text, type, duration });
  if (duration > 0) {
    timers.set(
      id,
      setTimeout(() => dismiss(id), duration)
    );
  }
  return id;
}

/**
 * Composable global de notificações por toast — singleton compartilhado entre telas.
 *
 * @remarks
 * Mantém uma fila única de toasts ativos. Cada toast auto-descarta após a duração
 * definida (default 3500ms), com opção de fechar manualmente. Use {@link push} para
 * emitir ou os atalhos {@link success}/{@link error}/{@link info}.
 *
 * @returns Estado reativo `toasts` e funções `push`, `success`, `error`, `info`, `warning`, `dismiss`.
 *
 * @example
 * ```typescript
 * const { success, warning } = useToast();
 * success("Dados sincronizados com Microsoft 365.");
 * warning("Alguns campos não puderam ser sincronizados.");
 * ```
 */
export function useToast() {
  return {
    toasts: readonly(toasts),
    push,
    success: (text: string, duration?: number) => push(text, "success", duration),
    error: (text: string, duration?: number) => push(text, "error", duration ?? 4000),
    info: (text: string, duration?: number) => push(text, "info", duration),
    warning: (text: string, duration?: number) => push(text, "warning", duration ?? 4000),
    dismiss,
  };
}